
import { Cloudinary, CloudinaryImage, CloudinaryVideo } from '@cloudinary/url-gen';
import { CLOUDINARY_CLOUD_NAME } from '@constants';

import { improve, sharpen } from '@cloudinary/transformation-builder-sdk/actions/adjust';
import { format, quality } from '@cloudinary/url-gen/actions/delivery';
import { fill } from '@cloudinary/url-gen/actions/resize';
import { byRadius } from '@cloudinary/url-gen/actions/roundCorners';
import { auto as autoFormat } from '@cloudinary/url-gen/qualifiers/format';
import { auto as autoQuality, autoBest, autoEco, autoGood, autoLow } from '@cloudinary/url-gen/qualifiers/quality';
import { autoGravity, compass } from '@cloudinary/url-gen/qualifiers/gravity';
import { Transformation, TRANSFORMATIONS } from '@services';
import { name } from '@cloudinary/url-gen/actions/namedTransformation';
import { text } from '@cloudinary/url-gen/qualifiers/source';
import { TextStyle } from '@cloudinary/url-gen/qualifiers/textStyle';
import { size } from '@cloudinary/url-gen/qualifiers/textFit';
import { source } from '@cloudinary/url-gen/actions/overlay';
import { Position } from '@cloudinary/url-gen/qualifiers';

interface ResizeParams {
  width: number;
  height: number;
  crop: 'fill';
}

interface TextOverlayParams {
  text: string;
  position: {
    x: number;
    y: number;
    anchor: string;
  },
  textStyle: {
    color?: string;
    size: number;
    bold?: boolean;
  }
}

export class CloudinaryIntegration {
  private readonly cloudinary;

  constructor() {
    this.cloudinary = new Cloudinary({
      cloud: { cloudName: CLOUDINARY_CLOUD_NAME }
    });
  }

  getPublicId(image: string): string | null {
    const publicId = image?.split('/').slice(-3).join('/');

    return publicId;
  }

  transformVideoById(publicId: string | null, transformations: Transformation[], videoURL = ''): string {
    if (!publicId) {
      return videoURL;
    }

    const cloudinaryVideo = this.cloudinary.video(publicId);

    transformations.forEach(transformation => {
      if (transformation.name === TRANSFORMATIONS.AUTO_FORMAT) {
        this.autoFormat(cloudinaryVideo);
      } else if (transformation.name === TRANSFORMATIONS.AUTO_QUALITY) {
        this.autoQuality(cloudinaryVideo, transformation.params as { auto?: 'best' | 'good' | 'eco' | 'low' });
      } else if (transformation.name === TRANSFORMATIONS.AUTO_SIZE) {
        this.autoSize(cloudinaryVideo, transformation.params as { width: number });
      } else if (transformation.name === TRANSFORMATIONS.RESIZE) {
        this.resize(cloudinaryVideo, transformation.params as ResizeParams);
      } else if (transformation.name === TRANSFORMATIONS.VIDEO_THUMBNAIL) {
        this.videoThumbnail(cloudinaryVideo);
      }
    });

    return cloudinaryVideo.setURLConfig({ forceVersion: false }).toURL();
  }

  transformVideo(videoURL: string, transformations: Transformation[]): string {
    const publicId = this.getPublicId(videoURL);

    return this.transformVideoById(publicId, transformations, videoURL);
  }

  transformById(publicId: string | null, transformations: Transformation[], image = ''): string {
    if (!publicId) {
      return image;
    }

    const cloudinaryImage = this.cloudinary.image(publicId);

    transformations.forEach(transformation => {
      if (transformation.name === TRANSFORMATIONS.SHARPEN) {
        this.sharpen(cloudinaryImage, transformation.params as { strength?: number });
      } else if (transformation.name === TRANSFORMATIONS.IMPROVE) {
        this.improve(cloudinaryImage, transformation.params as { mode?: 'outdoor' | 'indoor'; blend?: number; });
      } else if (transformation.name === TRANSFORMATIONS.AUTO_FORMAT) {
        this.autoFormat(cloudinaryImage);
      } else if (transformation.name === TRANSFORMATIONS.AUTO_QUALITY) {
        this.autoQuality(cloudinaryImage, transformation.params as { auto?: 'best' | 'good' | 'eco' | 'low' });
      } else if (transformation.name === TRANSFORMATIONS.AUTO_SIZE) {
        this.autoSize(cloudinaryImage, transformation.params as { width: number });
      } else if (transformation.name === TRANSFORMATIONS.RADIUS) {
        this.radius(cloudinaryImage, transformation.params as { radius: number });
      } else if (transformation.name === TRANSFORMATIONS.RESIZE) {
        this.resize(cloudinaryImage, transformation.params as ResizeParams);
      } else if (transformation.name === TRANSFORMATIONS.NAMED_TRASNFORMATION) {
        this.namedTransformation(cloudinaryImage, transformation.params as { name: string });
      } else if (transformation.name === TRANSFORMATIONS.TEXT_OVERLAY) {
        this.textOverlay(cloudinaryImage, transformation.params as TextOverlayParams);
      }
    });

    return cloudinaryImage.setURLConfig({ forceVersion: false }).toURL();
  }

  transform(image: string, transformations: Transformation[]): string {
    const publicId = this.getPublicId(image);

    return this.transformById(publicId, transformations, image);
  }

  sharpen(image: CloudinaryImage, params?: { strength?: number }): void {
    const adjustAction = params?.strength ? sharpen().strength(params.strength) : sharpen();
    image.adjust(adjustAction);
  }

  improve(image: CloudinaryImage, params?: { mode?: 'outdoor' | 'indoor', blend?: number}): void {
    let adjustAction = params?.blend ? improve().blend(params.blend) : improve();
    adjustAction = params?.mode ? adjustAction.mode(params.mode) : adjustAction;
    image.adjust(adjustAction);
  }

  autoFormat(image: CloudinaryImage | CloudinaryVideo): void {
    image.delivery(format(autoFormat()));
  }

  autoQuality(image: CloudinaryImage | CloudinaryVideo, params?: { auto?: 'best' | 'good' | 'eco' | 'low'}): void {
    let qualityAction;

    if (params?.auto) {
      if (params.auto === 'best') {
        qualityAction = quality(autoBest());
      } else if (params.auto === 'good') {
        qualityAction = quality(autoGood());
      } else if (params.auto === 'eco') {
        qualityAction = quality(autoEco());
      } else {
        qualityAction = quality(autoLow());
      }
    } else {
      qualityAction = quality(autoQuality());
    }

    image.delivery(qualityAction);
  }

  autoSize(image: CloudinaryImage | CloudinaryVideo, params: { width: number }): void {
    const sizeAction = fill(params.width);
    image.resize(sizeAction);
  }

  radius(image: CloudinaryImage, params: { radius: number }): void {
    const byRadiusAction = byRadius(params.radius);
    image.roundCorners(byRadiusAction);
  }

  resize(image: CloudinaryImage | CloudinaryVideo, params: ResizeParams): void {
    let sizeAction;
    switch (params.crop) {
    case 'fill':
      sizeAction = fill().width(params.width).height(params.height).gravity(autoGravity());
      break;
    default: break;
    }
    if (sizeAction) {
      image.resize(sizeAction);
    }
  }

  videoThumbnail(video: CloudinaryVideo): void {
    video.addTransformation('so_auto');
    video.format('jpg');
  }

  namedTransformation(image: CloudinaryImage, params: { name: string }): void {
    image.namedTransformation(name(params.name));
  }

  textOverlay(image: CloudinaryImage, params: TextOverlayParams): void {
    image.overlay(source(
      text(
        params.text,
        new TextStyle(
          params.textStyle.bold ? 'FiraSans-SemiBold.ttf' : 'FiraSans-Regular.ttf',
          params.textStyle.size
        )
      ).textColor(params.textStyle.color ?? 'black').textFit(size(270))
    ).position(
      new Position()
        .gravity(compass(params.position.anchor))
        .offsetX(params.position.x)
        .offsetY(params.position.y)
    )
    );
  }
}
